/** Fase 2 (W12): tipos do módulo Assinaturas e recorrência, como a API devolve. */
export const TIPOS_CONTRATO = ['recorrente', 'avulso'] as const;
export const FINS_CONTRATO = ['indeterminado', 'data', 'cobrancas'] as const;
export const MOMENTOS = ['vigente', 'subsequente'] as const;
export const INDICES = ['nenhum', 'igpm', 'ipca', 'inpc', 'selic'] as const;
export const ESTADOS = ['rascunho', 'ativo', 'pausado', 'encerrado'] as const;
/** Atalhos do intervalo na tela (em meses); "semanal" é 1 semana; "outro" abre o número e a unidade. */
export const INTERVALOS = [1, 3, 6, 12] as const;
/** C4: unidades do "repete a cada" (o antigo tinha dia, semana, mês e ano; ano é 12 meses). */
export const UNIDADES = ['dia', 'semana', 'mes'] as const;
export type Unidade = (typeof UNIDADES)[number];

export type EstadoContrato = (typeof ESTADOS)[number];
export type EstadoCobranca = 'agendada' | 'emitida' | 'falhou' | 'cancelada';

export const COR_ESTADO = { rascunho: 'neutro', ativo: 'verde', pausado: 'laranja', encerrado: 'perigo' } as const;
export const COR_COBRANCA = { agendada: 'azul', emitida: 'verde', falhou: 'perigo', cancelada: 'neutro' } as const;

export interface ItemContrato {
  servico_id: string | null;
  servico_nome?: string | null;
  descricao: string;
  quantidade: number;
  preco_centavos: number;
  custo_centavos: number;
  categoria_id: string | null;
  centro_custo_id: string | null;
  /** 22/10: recorrência própria do item; nula = a do contrato. */
  recorrencia?: RecorrenciaItem | null;
}

/** 22/10 (contrato por vertical): recorrências do item, na ordem da escolha. */
export const RECORRENCIAS_ITEM = ['avulso', 'mensal', 'trimestral', 'semestral', 'anual'] as const;
export type RecorrenciaItem = (typeof RECORRENCIAS_ITEM)[number];

export interface ContratoResumo {
  id: string;
  numero: string;
  referencia_externa: string | null;
  parceiro_id: string;
  /** Nulo quando o cliente é de uma empresa que a pessoa não vê (W20): a tela usa `nomeParceiro`. */
  parceiro_nome: string | null;
  /** 22/10: empresa (vertical) do contrato. */
  empresa_id?: string | null;
  vendedor_id: string | null;
  tipo: 'recorrente' | 'avulso';
  estado: EstadoContrato;
  inicio: string;
  primeira_cobranca: string;
  intervalo_meses: number;
  /** C4: mes (padrão), semana ou dia. */
  unidade_recorrencia: Unidade;
  centro_custo_id: string | null;
  fim_tipo: 'indeterminado' | 'data' | 'cobrancas';
  termino: string | null;
  indice: (typeof INDICES)[number];
  moeda: string;
  n_itens: number;
  valor_bruto_centavos: number;
  valor_ciclo_centavos: number;
  mrr_centavos: number;
  ultima_competencia: string | null;
  proxima_competencia: string | null;
  proxima_geracao: string | null;
  falhas: number;
  ultimo_erro: string | null;
  ultimo_erro_codigo: string | null;
  reajuste_pendente: boolean;
  /** Pacote 2: vencimento da fatura vencida mais antiga (contrato inadimplente) ou null. */
  inadimplente_desde: string | null;
  /** Pacote 2: plano de origem e o nome dele (nulo para quem não vê a empresa do plano). */
  plano_id: string | null;
  plano_nome: string | null;
  extras: Record<string, unknown>;
}

export interface Cobranca {
  id: string;
  competencia: string;
  /** C4: data do ciclo por dia ou semana (null no mensal). */
  periodo: string | null;
  ciclo: number;
  data_geracao: string;
  vencimento: string;
  bruto_centavos: number;
  desconto_centavos: number;
  valor_centavos: number;
  moeda: string;
  estado: EstadoCobranca;
  fatura_id: string | null;
  erro_codigo: string | null;
  erro: string | null;
  tentativas: number;
  motivo_cancelamento: string | null;
  reajuste_id: string | null;
}

export interface Contrato extends Omit<ContratoResumo, 'n_itens' | 'reajuste_pendente'> {
  /** Empresa do contrato (W2a). */
  empresa_id: string | null;
  fim_data: string | null;
  fim_cobrancas: number | null;
  dia_geracao: number;
  momento_pagamento: (typeof MOMENTOS)[number];
  dia_vencimento: number | null;
  forma_pagamento_id: string | null;
  condicao_pagamento_id: string | null;
  desconto_centavos: number;
  desconto_percentual: number;
  fator: string;
  observacoes_fatura: string | null;
  pausado_em: string | null;
  encerrado_em: string | null;
  motivo_encerramento: string | null;
  /** Pacote 2: dia da reativação depois de um encerramento. */
  reativado_em?: string | null;
  itens: ItemContrato[];
  /** Review r38 robustez (M2): as mais novas (até 40); as anteriores por página em `/assinaturas/contratos/:id/cobrancas`. */
  cobrancas: Cobranca[];
  cobrancas_anteriores_cursor: string | null;
  /** Agendadas ou com falha, em todas as páginas. */
  cobrancas_pendentes: number;
  reajustes: { id: string; ciclo: number; aniversario: string; indice: string; acumulado_pct: number; valor_antigo_centavos: number; valor_novo_centavos: number; estado: string; decidido_em: string | null }[];
  previsao: { competencia: string; periodo: string | null; data_geracao: string; vencimento: string }[];
}

export interface Reajuste {
  id: string;
  contrato_id: string;
  contrato_numero: string;
  parceiro_id: string;
  /** Nulo quando o cliente é de uma empresa que a pessoa não vê (W20): a tela usa `nomeParceiro`. */
  parceiro_nome: string | null;
  ciclo: number;
  aniversario: string;
  indice: string;
  periodo_de: string;
  periodo_ate: string;
  acumulado_pct: number;
  valor_antigo_centavos: number;
  valor_novo_centavos: number;
  moeda: string;
  estado: 'pendente' | 'aprovado' | 'recusado';
  decidido_em: string | null;
  cobrancas_ajustadas: number;
}

export interface LinhaMrr {
  moeda: string;
  mrr_centavos: number;
  arr_centavos: number;
  contratos: number;
  total: number;
  pausado_centavos: number;
  a_iniciar_centavos: number;
  novo_centavos: number;
  expansao_centavos: number;
  churn_centavos: number;
  /** Pacote 2: contratos que saíram com MRR no mês, reativação, e o MRR e os contratos inadimplentes (posição de hoje). */
  churn_contratos?: number;
  reativacao_centavos?: number;
  inadimplente_centavos?: number;
  inadimplentes?: number;
  inicial_centavos: number;
}

/** Pacote 2 (onda 1a): plano de assinatura (catálogo), como a API devolve. */
export interface ItemPlano {
  servico_id: string | null;
  servico_nome?: string | null;
  descricao: string;
  quantidade: number;
  preco_centavos: number;
}

export interface PlanoResumo {
  id: string;
  nome: string;
  descricao: string | null;
  tipo: 'recorrente' | 'avulso';
  intervalo_meses: number;
  unidade_recorrencia: Unidade;
  moeda: string;
  ativo: boolean;
  empresa_id: string | null;
  n_itens: number;
  preco_centavos: number;
  contratos_ativos: number;
}

export interface Plano extends Omit<PlanoResumo, 'n_itens'> {
  itens: ItemPlano[];
}

export const CAMINHO_PLANOS = '/assinaturas/planos';
export const CAMINHO_CONTRATOS = '/assinaturas/contratos';
export const CAMINHO_REAJUSTES = '/assinaturas/reajustes';

// ----- cálculo de tela (prévia antes de salvar; o servidor é quem vale) ---------------------------------------------

const p2 = (n: number) => String(n).padStart(2, '0');
const ultimoDia = (a: number, m: number) => new Date(Date.UTC(a, m, 0)).getUTCDate();
function somarDias(data: string, n: number) {
  const [a, m, d] = data.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
}
/** C4: meses equivalentes a um ciclo (divisor do MRR), como o servidor: por dia ou semana, pelo mês médio (365,25 ÷ 12). */
export function mesesDoCiclo(intervalo: number, unidade: string = 'mes') {
  return unidade === 'dia' || unidade === 'semana' ? (intervalo * (unidade === 'semana' ? 7 : 1) * 12) / 365.25 : intervalo;
}
function somarMes(mes: string, n: number) {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  const t = a * 12 + m - 1 + n;
  return `${Math.floor(t / 12)}-${p2((t % 12) + 1)}`;
}
function noMes(mes: string, dia: number) {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  return `${mes}-${p2(Math.min(dia, ultimoDia(a, m)))}`;
}

export interface RegraTela {
  tipo: string;
  primeira_cobranca: string;
  intervalo_meses: number;
  /** C4: ausente é mês. */
  unidade_recorrencia?: string;
  fim_tipo: string;
  fim_data: string | null;
  fim_cobrancas: number | null;
  dia_geracao: number;
  momento_pagamento: string;
  dia_vencimento: number | null;
}

/** As próximas datas pelo calendário (W12.5), para a prévia do formulário. */
export function previaDoCalendario(r: RegraTela, limite = 12): { competencia: string; periodo: string | null; data_geracao: string; vencimento: string }[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.primeira_cobranca) || !(r.intervalo_meses >= 1)) return [];
  const saida = [];
  // C4: por dia ou semana, a fatura nasce na data do ciclo e vence nela (vigente) ou no ciclo seguinte (subsequente).
  if (r.tipo !== 'avulso' && (r.unidade_recorrencia === 'dia' || r.unidade_recorrencia === 'semana')) {
    const passo = r.intervalo_meses * (r.unidade_recorrencia === 'semana' ? 7 : 1);
    for (let k = 0; k < limite; k++) {
      if (r.fim_tipo === 'cobrancas' && k >= (r.fim_cobrancas ?? 0)) break;
      const periodo = somarDias(r.primeira_cobranca, k * passo);
      if (r.fim_tipo === 'data' && r.fim_data && periodo > r.fim_data) break;
      const vencimento = r.momento_pagamento === 'subsequente' ? somarDias(periodo, passo) : periodo;
      saida.push({ competencia: periodo.slice(0, 7), periodo, data_geracao: periodo, vencimento });
    }
    return saida;
  }
  for (let k = 0; k < limite; k++) {
    if (r.tipo === 'avulso' && k > 0) break;
    if (r.fim_tipo === 'cobrancas' && k >= (r.fim_cobrancas ?? 0)) break;
    const competencia = somarMes(r.primeira_cobranca.slice(0, 7), k * r.intervalo_meses);
    if (r.fim_tipo === 'data' && r.fim_data && competencia > r.fim_data.slice(0, 7)) break;
    const mesVenc = r.momento_pagamento === 'subsequente' ? somarMes(competencia, 1) : competencia;
    const vencimento = noMes(mesVenc, r.dia_vencimento ?? Number(r.primeira_cobranca.slice(8, 10)));
    let geracao = noMes(mesVenc, r.dia_geracao || 1);
    if (geracao > vencimento) geracao = noMes(somarMes(mesVenc, -1), r.dia_geracao || 1);
    saida.push({ competencia, periodo: null, data_geracao: geracao, vencimento });
  }
  return saida;
}

/** Total da linha com arredondamento comercial (meio para cima), como o servidor. */
export const totalDaLinha = (quantidade: number, preco: number) => {
  const q = Math.round(quantidade * 10_000);
  const produto = q * preco;
  const inteiro = Math.floor(produto / 10_000);
  return produto - inteiro * 10_000 >= 5_000 ? inteiro + 1 : inteiro;
};

export function valorDaTela(itens: { quantidade: number; preco_centavos: number }[], descontoCentavos: number, descontoPct: number) {
  const bruto = itens.reduce((a, i) => a + totalDaLinha(i.quantidade, i.preco_centavos), 0);
  const desconto = Math.min(bruto, descontoPct > 0 ? totalDaLinha(descontoPct / 100, bruto) : descontoCentavos);
  return { bruto, desconto, liquido: bruto - desconto };
}

const MESES_DA_RECORRENCIA: Record<RecorrenciaItem, number | null> = { avulso: null, mensal: 1, trimestral: 3, semestral: 6, anual: 12 };

/**
 * 22/10: MRR na tela, como o `mensalDoContrato` do servidor. Sem item com recorrência própria, líquido ÷ meses do ciclo; com,
 * cada periodicidade com o líquido dela ÷ meses (o avulso fica fora; o desconto em valor vai na menor periodicidade).
 */
export function mrrDaTela(itens: { quantidade: number; preco_centavos: number; recorrencia?: RecorrenciaItem | '' | null }[], descontoCentavos: number, descontoPct: number, mesesContrato: number) {
  if (!itens.some((i) => i.recorrencia)) return Math.round(valorDaTela(itens, descontoCentavos, descontoPct).liquido / mesesContrato);
  const grupos = new Map<number, number>();
  for (const i of itens) {
    const m = i.recorrencia ? MESES_DA_RECORRENCIA[i.recorrencia] : mesesContrato;
    if (m !== null) grupos.set(m, (grupos.get(m) ?? 0) + totalDaLinha(i.quantidade, i.preco_centavos));
  }
  if (!grupos.size) return 0;
  const menor = Math.min(...grupos.keys());
  let total = 0;
  for (const [m, bruto] of grupos) total += (bruto - (descontoPct > 0 ? totalDaLinha(descontoPct / 100, bruto) : m === menor ? Math.min(descontoCentavos, bruto) : 0)) / m;
  return Math.round(total);
}
